/**
 * Ce que l'association RECEVRA d'un don, affiché AVANT le paiement
 * (« L'association reçoit … » des pages de don et de cagnotte).
 *
 * 🔴 Bug du 14/10/2026 : la ligne affichait le don choisi tel quel (« reçoit
 * 10 € ») même quand le donateur ne couvrait PAS les frais — or l'association
 * en reçoit moins. Montant faux sous les yeux du donateur.
 *
 * Miroir de `calculateFees` côté API (payment/utils/feeCalculator.ts), sans
 * le modifier :
 *  - frais couverts : l'association reçoit le don entier ;
 *  - frais NON couverts : don − frais du prestataire − commission Donoor.
 *    Les frais du prestataire portent sur TOUT ce que paie le donateur,
 *    soutien à Donoor compris (c'est ainsi que Stripe/PayPal prélèvent) ;
 *    la commission Donoor, sur le don seul.
 *
 * Estimation : le montant réel n'est connu qu'au paiement (frais exacts de
 * Stripe/PayPal, carte hors Europe…). Même taux que ceux proposés au
 * donateur dans « Couvrir les frais ».
 *
 * Affichage seulement : rien ici n'entre dans le montant débité.
 */
import { STRIPE_EU_FIXED_FEE, STRIPE_EU_RATE } from "@donoor/utils";

export interface EntreeNetAssociation {
  /** Don choisi pour l'association, en euros. */
  montant: number;
  /** Soutien volontaire à Donoor, en euros (0 si aucun). */
  soutienDonoor: number;
  /** Le donateur couvre-t-il les frais ? */
  couvreFrais: boolean;
  /** Commission Donoor, en décimal (0,015 = 1,5 %). */
  tauxDonoor: number;
  /** Prestataire qui encaissera. */
  prestataire?: "stripe" | "paypal";
  /** Taux PayPal appris, EN POURCENT (2,9 = 2,9 %). */
  paypalPourcent?: number;
  /** Part fixe PayPal, en euros. */
  paypalFixe?: number;
}

const arrondi2 = (n: number) => Math.round(n * 100) / 100;

/** Taux et part fixe du prestataire — mêmes règles que l'estimation « Couvrir les frais ». */
function fraisPrestataire(e: EntreeNetAssociation): { taux: number; fixe: number } {
  if (e.prestataire === "paypal") {
    const pct = e.paypalPourcent;
    const fixe = e.paypalFixe;
    if (typeof pct === "number" && pct > 0) {
      return { taux: pct / 100, fixe: typeof fixe === "number" && fixe >= 0 ? fixe : STRIPE_EU_FIXED_FEE };
    }
  }
  return { taux: STRIPE_EU_RATE, fixe: STRIPE_EU_FIXED_FEE };
}

/** Ce que l'association reçoit, et ce qui en est retiré (en euros). */
export interface DetailNetAssociation {
  /** Net reçu par l'association. */
  net: number;
  /** Frais du prestataire retirés (0 si le donateur les couvre). */
  frais: number;
  /** Commission Donoor retirée (0 si couverte ou sans commission). */
  commission: number;
}

/** Net estimé pour l'association et son détail (0 partout si aucun montant). */
export function detailNetAssociation(e: EntreeNetAssociation): DetailNetAssociation {
  const montant = Number(e.montant) || 0;
  if (montant <= 0) return { net: 0, frais: 0, commission: 0 };
  if (e.couvreFrais) return { net: montant, frais: 0, commission: 0 };

  const soutien = Math.max(0, Number(e.soutienDonoor) || 0);
  const { taux, fixe } = fraisPrestataire(e);
  const frais = arrondi2((montant + soutien) * taux + fixe);
  const commission = arrondi2(montant * (Number(e.tauxDonoor) || 0));
  return { net: Math.max(0, arrondi2(montant - frais - commission)), frais, commission };
}

/** Net estimé pour l'association, en euros (0 si aucun montant choisi). */
export function netAssociationEstime(e: EntreeNetAssociation): number {
  return detailNetAssociation(e).net;
}
